import React, {useMemo} from 'react';

import {maybePluralize} from '../../../util/uihelpers';
import {BULK_ADD_LIMIT_MULTI_METRIC} from './constants';
import {MultiMetricPanelsMap} from './hooks/useMultiMetricPanelsMap';
import {MultiMetricPanelsListItem} from './MultiMetricPanelsListItem';

type MultiMetricPanelsListProps = {
  panelsToAdd: MultiMetricPanelsMap;
  numNotAdded: number;
  numAlreadyAdded: number;
  isPanelLimitExceeded: boolean;
};

export const MultiMetricPanelsList = ({
  panelsToAdd,
  numNotAdded,
  numAlreadyAdded,
  isPanelLimitExceeded,
}: MultiMetricPanelsListProps) => {
  // Memoizing because panelsToAdd size could get quite large
  const panelRegexes = useMemo(() => {
    return Array.from(panelsToAdd.keys()).sort((a, b) => {
      // sort panels that have already been added to bottom of list
      const aAdded = panelsToAdd.get(a)?.isAlreadyAdded ?? false;
      const bAdded = panelsToAdd.get(b)?.isAlreadyAdded ?? false;
      return aAdded ? 1 : bAdded ? -1 : 0;
    });
  }, [panelsToAdd]);

  let title;
  if (numNotAdded === 0) {
    title = 'No panels to add';
  } else if (isPanelLimitExceeded) {
    title = `Over ${BULK_ADD_LIMIT_MULTI_METRIC} panels`;
  } else {
    title = `${maybePluralize(numNotAdded, 'panel')} will be added`;
  }

  return (
    <div>
      <p className="text-moon-800 font-semibold mb-8">
        {title}
        {numAlreadyAdded > 0 && (
          <span className="text-moon-500 font-normal">
            {' '}
            ({maybePluralize(numAlreadyAdded, 'panel')} already in workspace)
          </span>
        )}
      </p>
      <div className="ml-12 border-l-2 border-solid border-moon-250 pl-12">
        <div className="flex flex-col gap-8">
          {panelRegexes.map(panelRegex => {
            const panelToAdd = panelsToAdd.get(panelRegex);
            if (!panelToAdd) {
              return null;
            }
            return (
              <MultiMetricPanelsListItem
                key={panelRegex}
                regex={panelRegex}
                metrics={panelToAdd.metrics}
                isAlreadyAdded={panelToAdd.isAlreadyAdded}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};
